import Quill, { Delta } from 'quill';
import {
  Injector,
  ApplicationRef,
  ComponentFactoryResolver,
} from '@angular/core';
import { CustomCalendarComponent } from '../../common-components/custom-calendar/custom-calendar.component';
const BlockEmbed: any = Quill.import('blots/block/embed');
const Embed: any = Quill.import('blots/embed');
const icons: any = Quill.import('ui/icons');
icons['hr'] =
  '<svg viewBox="0 0 18 18"><line class="ql-stroke" x1="3" y1="9" x2="15" y2="9"></line></svg>';
class HrBlot extends BlockEmbed {
  static blotName = 'hr';
  static tagName = 'hr';
  static create(value: any) {
    const node = super.create();
    if (typeof value === 'object' && value.color) {
      node.setAttribute(
        'style',
        `border-top: 1px solid; color:${value.color};`,
      );
    } else {
      node.setAttribute('style', 'border-top: 1px solid; color:black;');
    }
    return node;
  }

  static value(node: any) {
    return {
      color: node.style.color || 'black',
    };
  }
}
class SoftLineBreakBlot extends Embed {
  static blotName = 'softbreak';
  static tagName = 'br';
  static className = 'softbreak';
}
class TextInputBlot extends Embed {
  static blotName = 'textinput';
  static tagName = 'input';

  static create(value: any) {
    const node = super.create();
    node.setAttribute('type', 'text');
    node.setAttribute('value', value || '');
    return node;
  }

  static value(node: any) {
    return node.getAttribute('value');
  }
}

class CheckboxBlot extends Embed {
  static blotName = 'checkbox';
  static tagName = 'input';

  static create(value: any) {
    const node = super.create();
    node.setAttribute('type', 'checkbox');
    if (value?.checked) {
      node.setAttribute('checked', true);
    }
    if (value?.name) {
      node.setAttribute('name', value.name);
    }
    if (value?.id) {
      node.setAttribute('id', value.id);
    }
    if (value?.disabled) {
      node.setAttribute('disabled', value.disabled);
    }
    return node;
  }

  static value(node: any) {
    return {
      checked: node.getAttribute('checked'),
      value: node.getAttribute('value'),
      name: node.getAttribute('name'),
      id: node.getAttribute('id'),
      disabled: node.getAttribute('disabled'),
    };
  }
}

class RadioButtonBlot extends Embed {
  static blotName = 'radiobutton';
  static tagName = 'input';

  static create(value: any) {
    const node = super.create();
    node.setAttribute('type', 'radio');
    node.setAttribute('value', value?.value || '');
    if (value?.checked) {
      node.setAttribute('checked', true);
    }
    if (value?.name) {
      node.setAttribute('name', value.name);
    }
    if (value?.id) {
      node.setAttribute('id', value.id);
    }
    if (value?.disabled) {
      node.setAttribute('disabled', value.disabled);
    }
    return node;
  }

  static value(node: any) {
    return {
      checked: node.getAttribute('checked'),
      value: node.getAttribute('value'),
      name: node.getAttribute('name'),
      id: node.getAttribute('id'),
      disabled: node.getAttribute('disabled'),
    };
  }
}
class DateInputBlot extends Embed {
  static blotName = 'dateinput';
  static tagName = 'div';

  static injector: Injector;

  static create(value: any) {
    const node = super.create();
    node.setAttribute('class', 'custom-calendar-container');

    if (!DateInputBlot.injector) {
      throw new Error('Angular Injector is not set for DateInputBlot.');
    }

    const appRef: ApplicationRef = DateInputBlot.injector.get(ApplicationRef);
    const resolver: ComponentFactoryResolver = DateInputBlot.injector.get(
      ComponentFactoryResolver,
    );
    const factory = resolver.resolveComponentFactory(CustomCalendarComponent);

    const componentRef = factory.create(appRef.components[0].injector);
    appRef.attachView(componentRef.hostView);

    if (value?.value) {
      componentRef.instance.value = value.value;
    }
    componentRef.instance.showIcon = true;
    componentRef.instance.showButtonBar = true;
    componentRef.instance.placeholder = 'mm/dd/yyyy';
    componentRef.instance.readonlyInput = value.disabled;
    componentRef.instance.maxDate = new Date();

    node.appendChild(componentRef.location.nativeElement);

    componentRef.instance.valueChange.subscribe((newValue: any) => {
      const customEvent = new CustomEvent('change', {
        detail: {
          value: newValue,
          id: value.id,
          name: value.name,
          type: 'date',
          disabled: value.disabled,
        },
      });
      node.parentNode.parentNode.dispatchEvent(customEvent);
    });

    return node;
  }

  static value(node: any) {
    const calendarElement = node.querySelector('.custom-calendar-container');
    return {
      value: calendarElement?.getAttribute('value'),
      name: calendarElement?.getAttribute('name'),
      id: calendarElement?.getAttribute('id'),
      disabled: calendarElement?.getAttribute('disabled'),
    };
  }
}

export function setAngularInjector(injector: Injector) {
  DateInputBlot.injector = injector;
}

Quill.register(DateInputBlot);
Quill.register(TextInputBlot);
Quill.register(CheckboxBlot);
Quill.register(RadioButtonBlot);

Quill.register(HrBlot);
Quill.register(SoftLineBreakBlot);

export function inputMatcher(node: any, delta: any) {
  const newDelta = new Delta();
  if (node.type === 'text') {
    newDelta.insert({
      textinput: node.value,
      id: node.id,
      name: node.name,
      disabled: node.disabled,
    });
  } else if (node.type === 'checkbox') {
    newDelta.insert({
      checkbox: {
        checked: node.checked,
        value: node.value,
        id: node.id,
        name: node.name,
        disabled: node.disabled,
      },
    });
  } else if (node.type === 'radio') {
    newDelta.insert({
      radiobutton: {
        checked: node.checked,
        value: node.value,
        id: node.id,
        name: node.name,
        disabled: node.disabled,
      },
    });
  } else if (node.type === 'date') {
    newDelta.insert({
      dateinput: {
        value: node.defaultValue,
        id: node.id,
        name: node.name,
        disabled: node.disabled,
      },
    });
  }
  return newDelta;
}

export function brMatcher(node: any, delta: any) {
  const newDelta = new Delta();
  newDelta.insert({ softbreak: true });
  return newDelta;
}

export interface CustomBlotEvent {
  type: string;
  value: any;
  id?: string;
  name?: string;
  disabled?: boolean;
}
